import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import ReviewQueueStore from "./ReviewQueueStore";

/**
 * Meeting Intelligence → **Work Status**.
 *
 * The route NAME (`ReviewQueue`) and PATH (`/review-queue`) are wire formats
 * and are deliberately unchanged: the name is what `routerStore.goTo` and the
 * Command Center's attention list key on, and the path is what Administration →
 * Page Visibility stores its deny rows against (`layout + path`). Renaming
 * either would orphan those rows and break every internal link for a cosmetic
 * gain. What a HUMAN reads — the title, the nav label, the search aliases — is
 * what changed, and "Review Queue" is no longer one of the things it says,
 * because there is nothing here to review: approval was removed on 2026-09-18.
 */
const reviewQueueRoute: RouteType[] = [
  {
    name: "ReviewQueue",
    path: "/review-queue",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Work Status",
        enableRefreshButton: true,
        component: () => import("./ReviewQueue.Header"),
      },
      body: {
        component: () => import("./ReviewQueue.Body"),
        grayBackground: true,
      },
    },
    group: "Meeting Intelligence",
    // "review queue" and "approvals" stay as ALIASES — somebody who learned
    // the old name must still land here and read why it is gone, rather than
    // searching, finding nothing, and assuming the screen was deleted.
    searchAliases: [
      "action items",
      "assignments",
      "extracted tasks",
      "review queue",
      "approvals",
      "accepted work",
      "work items",
    ],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      const store = rootStore.reviewQueueStore as ReviewQueueStore;
      // `?client=<id>` (the Whiteboard's "Open in Work Status") narrows the
      // list to that client; without it, everyone's work.
      store?.showClientFromLink(rootStore.routerStore?.queryParams?.client);
      store?.load();
      store?.loadUsers();
      store?.loadModules();
      store?.subscribe();
    },
    onExit: ({ rootStore }) => {
      const store = rootStore.reviewQueueStore as ReviewQueueStore;
      store?.dispose();
    },
    dialogs: [
      {
        name: "ReadAiReassignDialog",
        title: "Reassign action item",
        submitButtonText: "Reassign",
        component: () => import("./dialogs/ReassignDialog.Body"),
        cancelButton: true,
        height: 300,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused reassign throws, so the dialog stays open with the
          // reason in its footer (shared/dialogSubmit).
          const store = rootStore.reviewQueueStore as ReviewQueueStore;
          throwIfFailed(
            await store.submitReassign(),
            store.reassignError,
            "The action item could not be reassigned."
          );
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
          !!(rootStore.reviewQueueStore as ReviewQueueStore).reassignTargetUserId,
      },
    ],
  },
];

export default reviewQueueRoute;
